import React from 'react';
import { Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import {
  RequestStatusBadge,
  StatusBadgeWithLoader,
} from 'src/features/requests/request-status-badge';
import {
  isInReeditCycle,
  resolveDisplayedRequestStatus,
} from 'src/features/requests/request.helper';
import { requestStatus, RequestsTableResponse } from 'src/lib/services/api/request-api';
import {
  UploadingStatusCell,
  useBackgroundUploadStore,
} from 'src/features/requests/background-upload';

type Props = {
  request: Pick<
    RequestsTableResponse,
    | 'id'
    | 'status'
    | 'requested_attention_at'
    | 'requested_reedit_at'
    | 'reedit_count'
    | 'approved_at'
    | 'revisions_count'
    | 'rated_revisions_count'
  > &
    Partial<Pick<RequestsTableResponse, 'auto_approves_at' | 'archived_at' | 'files_delete_at' | 'files_deleted_at'>>;
  size?: 'sm' | 'default';
  /**
   * Show the File Retention line under the pill: "Auto-approves in N days" on
   * Ready for review (last 10 days), "Available until <date> · N days left" on
   * Archived.
   */
  showAutoApprove?: boolean;
  /** Requests table rows: the pill styled like Admin's (#2756). */
  tableLook?: boolean;
  /**
   * Revisions page breadcrumb row: the line sits beside the pill, not under
   * it, and may wrap on a narrow screen (#2772).
   */
  inline?: boolean;
};

/**
 * "Available until <date> · N days left" under an Archived pill. Plain until 30
 * days are left, amber from 30, red from 7 (File Retention PRD).
 */
const DAY_MS = 24 * 60 * 60 * 1000;
// The date as stored (UTC), so every viewer sees the same day.
const utcDay = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

// The stored UTC day in the user's locale; '' for an unparseable value rather than a crash.
const formatUtcDate = (value: string, language?: string): string => {
  const date = parseISO(value);
  if (!isValid(date)) {
    return '';
  }

  return new Intl.DateTimeFormat(language || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

/** "Files were deleted on <date>" once File Retention has deleted them (#2749). */
const FilesDeletedNotice: React.FC<{ filesDeletedAt: string }> = ({ filesDeletedAt }) => {
  const { t, i18n } = useTranslation();

  return (
    <span className={'tw-text-xs tw-whitespace-nowrap tw-text-muted-foreground'}>
      {t('files_were_deleted_on', {
        date: formatUtcDate(filesDeletedAt, i18n.language),
        defaultValue: 'Files were deleted on {{date}}',
      })}
    </span>
  );
};

/** From this many days left the countdown is red, the row is flagged and the dashboard lists it (#2772). */
export const FILES_DELETE_URGENT_DAYS = 7;
const FILES_DELETE_WARNING_DAYS = 30;

/**
 * Calendar days (UTC) until a request's files are deleted, or null when there's
 * no countdown: no date, already deleted, or past the date (the nightly job
 * runs before files_deleted_at switches it to "Files were deleted on").
 */
export const filesDeleteDaysLeft = (
  request: Partial<Pick<RequestsTableResponse, 'files_delete_at' | 'files_deleted_at'>>,
): number | null => {
  if (!request.files_delete_at || request.files_deleted_at) {
    return null;
  }
  const date = parseISO(request.files_delete_at);
  if (!isValid(date)) {
    return null;
  }
  const days = Math.round((utcDay(date) - utcDay(new Date())) / DAY_MS);

  return days < 0 ? null : days;
};

/**
 * An approved request archived by the approval clock, in its last 7 days: the
 * row is flagged and the dashboard lists it (#2772). Requests and outcomes the
 * client deleted or removed only get the red countdown - they chose to discard
 * them. Matches be-core's `files_deleting_soon` filter
 * (config retention.dashboard_warning_days).
 */
export const isFilesDeletionUrgent = (
  request: Partial<
    Pick<RequestsTableResponse, 'files_delete_at' | 'files_deleted_at' | 'archive_reason'>
  >,
): boolean => {
  const days = filesDeleteDaysLeft(request);

  return (
    request.archive_reason === 'approval_clock' &&
    days !== null &&
    days <= FILES_DELETE_URGENT_DAYS
  );
};

export const FilesDeleteNotice: React.FC<{ filesDeleteAt?: string | null }> = ({ filesDeleteAt }) => {
  const { t, i18n } = useTranslation();
  const days = filesDeleteDaysLeft({ files_delete_at: filesDeleteAt });
  if (!filesDeleteAt || days === null) {
    return null;
  }
  const tone =
    days <= FILES_DELETE_URGENT_DAYS
      ? 'tw-text-red-600'
      : days <= FILES_DELETE_WARNING_DAYS
        ? 'tw-text-amber-600'
        : 'tw-text-muted-foreground';
  const on = formatUtcDate(filesDeleteAt, i18n.language);

  return (
    <span className={`tw-text-xs tw-whitespace-nowrap ${tone}`}>
      {/* "Available until", not "will be deleted", so it reads apart from
          "Files were deleted on" (Toma, #2752). */}
      {days === 0
        ? t('files_available_until_today', { defaultValue: 'Available until today' })
        : t('files_available_until_days_left', {
            count: days,
            date: on,
            defaultValue_one: 'Available until {{date}} · {{count}} day left',
            defaultValue_other: 'Available until {{date}} · {{count}} days left',
          })}
    </span>
  );
};

// The countdown appears from day 20 of the 30 (T&C 10(a), File Retention PRD).
const AUTO_APPROVE_NOTICE_DAYS = 10;

/**
 * Calendar days until auto-approval in the viewer's timezone, or null when
 * there's nothing to show (no date, more than 10 days away, or already past -
 * an overdue date means the nightly job hasn't caught up, not "today").
 */
const autoApproveDaysLeft = (autoApprovesAt?: string | null): number | null => {
  if (!autoApprovesAt) {
    return null;
  }
  const days = differenceInCalendarDays(parseISO(autoApprovesAt), new Date());
  return days >= 0 && days <= AUTO_APPROVE_NOTICE_DAYS ? days : null;
};

const AutoApproveNotice: React.FC<{ autoApprovesAt?: string | null }> = ({ autoApprovesAt }) => {
  const { t } = useTranslation();
  const days = autoApproveDaysLeft(autoApprovesAt);
  if (days === null) {
    return null;
  }
  return (
    <span className={'tw-text-xs tw-text-muted-foreground tw-whitespace-nowrap'}>
      {days === 0
        ? t('auto_approves_today', { defaultValue: 'Auto-approves today' })
        : t('auto_approves_in_days', {
            count: days,
            defaultValue_one: 'Auto-approves in {{count}} day',
            defaultValue_other: 'Auto-approves in {{count}} days',
          })}
    </span>
  );
};

/**
 * Renders the status pill for a request row.
 *
 * Render paths (highest priority first):
 *  1. status === 'uploading' → {@link UploadingStatusCell} (progress + retry).
 *  2. status === 'queued' AND a background upload is still in flight for
 *     this request → Queued pill with a Loader2 icon INSIDE the pill so
 *     the user sees the upload is still progressing even after the
 *     status flipped to queued. (Karina #2372 follow-up.)
 *  3. Otherwise → derived pill via `resolveDisplayedRequestStatus`,
 *     which overrides with `needs_attention` or `needs_reedit` when the
 *     row's flags are set. While the row is in the re-edit cycle a
 *     static `Repeat` icon trails inside the pill (`Needs Re-edit →
 *     Editing → Ready for review`) so the "still in cycle" signal
 *     persists across status flips. (#2383, #2442.) It's static rather
 *     than a spinner because it signals "went through a re-edit cycle",
 *     not "loading" — a spinning icon on a "Ready for review" pill read
 *     to clients as the request being stuck (#164).
 *
 * Re-edit icon is suppressed when the displayed pill is `needs_attention`.
 * The Needs Attention overlay is urgency-first and supersedes the re-edit
 * pill: stacking the re-edit icon on top of an attention pill reads as
 * "Needs Attention is in re-edit" — nonsense to the user (#2462 Karina).
 * The underlying cycle state is unchanged; once attention is resolved the
 * pill drops back to its underlying status and the icon resumes naturally
 * if a re-edit is still in flight.
 */
const RequestStatusCell: React.FC<Props> = ({
  request,
  size = 'sm',
  showAutoApprove = false,
  tableLook = false,
  inline = false,
}) => {
  const look = tableLook ? 'table' : 'default';
  const { id: requestId, status } = request;
  const hasActiveBackgroundUpload = useBackgroundUploadStore(
    (s) => !!s.activeRequests[requestId],
  );

  if (status === requestStatus.uploading) {
    return <UploadingStatusCell requestId={requestId} size={size} />;
  }

  if (status === requestStatus.queued && hasActiveBackgroundUpload) {
    return <StatusBadgeWithLoader size={size} look={look} variant={status} />;
  }

  const displayedStatus = resolveDisplayedRequestStatus(request);
  const showReeditIndicator =
    isInReeditCycle(request) && displayedStatus !== 'needs_attention';

  const badge = (
    <RequestStatusBadge
      size={size}
      look={look}
      variant={displayedStatus}
      iconPrefix={
        showReeditIndicator ? (
          <Repeat size={12} className={'tw-shrink-0'} aria-hidden={'true'} />
        ) : undefined
      }
    />
  );

  // Gate on the displayed pill: a Needs Attention / Re-edit overlay takes priority.
  if (!showAutoApprove) {
    return badge;
  }

  // Inline lets the notice (second child, after the pill) wrap: its span is
  // nowrap for the stacked look.
  const lineLayout = inline
    ? 'tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 [&>span:nth-child(2)]:tw-whitespace-normal'
    : 'tw-flex tw-flex-col tw-items-start tw-gap-1';

  // Deleted files are permanent news, whatever the pill shows (a removed
  // request has a delete date but no archive date, a reopened one loses it).
  if (request.files_deleted_at) {
    return (
      <div className={lineLayout}>
        {badge}
        <FilesDeletedNotice filesDeletedAt={request.files_deleted_at} />
      </div>
    );
  }

  if (displayedStatus === requestStatus.delivered) {
    return (
      <div className={lineLayout}>
        {badge}
        <AutoApproveNotice autoApprovesAt={request.auto_approves_at} />
      </div>
    );
  }

  if (displayedStatus === 'archived' && request.archived_at) {
    return (
      <div className={lineLayout}>
        {badge}
        <FilesDeleteNotice filesDeleteAt={request.files_delete_at} />
      </div>
    );
  }

  return badge;
};

export { RequestStatusCell, formatUtcDate };
